import { Buffer } from "buffer";

import { EventContextProvider, useAnalytics } from "@codat/analytics";
import { Typography, ZeroState, useToast } from "@codat/orchard-ui";
import {
  useGetMaskedClientProfileQuery,
  usePutClientProfileMutation,
} from "api/public";
import { useFeatureStatus } from "portal-ui-remote/FeatureStatusContext";
import React, { useEffect, useState } from "react";
import { Redirect } from "react-router-dom";

import { paths } from "components/App/paths";
import Page from "components/Page";
import PageHeader from "components/PageHeader";

import { AuthHeader } from "./AuthHeaderType";
import AlertSettingsForm from "./Form";

export const parseHeader = (header: string | null | undefined): AuthHeader => {
  if (!header) {
    return {
      scheme: "None",
    };
  } else if (header.indexOf("Basic") > -1) {
    const value = Buffer.from(header.replace(/Basic/, "").trim(), "base64")
      .toString()
      .split(":");
    return {
      scheme: "Basic",
      basic: {
        username: value[0],
        password: value[1],
      },
    };
  } else if (header.indexOf("Bearer") > -1) {
    const value = header.replace(/Bearer/, "").trim();
    return {
      scheme: "Bearer",
      bearer: value,
    };
  } else {
    return {
      scheme: "Custom",
      custom: header,
    };
  }
};

export const buildHeader = (header: AuthHeader) => {
  switch (header.scheme) {
    case "Basic":
      return `Basic ${Buffer.from(
        header.basic?.username + ":" + header.basic?.password
      ).toString("base64")}`;
    case "Bearer":
      return `Bearer ${header.bearer}`;
    case "Custom":
      return header.custom;
    default:
      return null;
  }
};

const WebhookSettingsContent: React.FC = () => {
  const { sendEvent } = useAnalytics();
  const { featureStatus } = useFeatureStatus();
  const [{ data, error }, getClientProfile] = useGetMaskedClientProfileQuery();
  const [header, setHeader] = useState<AuthHeader>();

  const [{ loading: isUpdating }, updateProfile] =
    usePutClientProfileMutation();
  const { addToast } = useToast();

  useEffect(() => {
    if (data) {
      const parsedHeader = parseHeader(data.alertAuthHeader);
      setHeader(parsedHeader);
    }
  }, [data]);

  const isLoading = !header;
  const isUsingNewWebhooks = featureStatus?.["Rules.UseNewWebhooks"] ?? false;

  if (isUsingNewWebhooks) {
    return <Redirect to={paths.monitor.EVENTS} />;
  }

  if (error) {
    return (
      <ZeroState
        data-testid="webhook-security-error-state"
        title="Error loading data, please try again."
        ctaText="Reload data"
        onCtaClick={() => {
          sendEvent("Zero State - Reload Data Button - Click");
          getClientProfile();
        }}
      />
    );
  }

  return (
    <>
      <Typography as="h4" variant="heading_lg">
        Webhook Authorization Header
      </Typography>
      <Typography as="p" variant="body_md">
        If specified, the given authorization information will be added as an
        &apos;Authorization&apos; type header to all requests made to a webhook
        notifier.
      </Typography>
      <Typography as="h5" variant="heading_sm">
        Authorization Scheme
      </Typography>

      <AlertSettingsForm
        defaultValues={header}
        isUpdating={isUpdating}
        isLoading={isLoading}
        onSubmit={(values) => {
          updateProfile({
            data: {
              ...data,
              alertAuthHeader: buildHeader(values),
            },
          })
            .then(() => {
              addToast({
                message: "Updated webhook settings.",
                variant: "success",
              });
            })
            .catch(() => {
              addToast({
                message: "Failed to update webhook settings.",
                variant: "error",
              });
            });
        }}
      />
    </>
  );
};

export const WebhookSettings = () => {
  return (
    <Page>
      <EventContextProvider name="Webhook Security">
        <PageHeader header="Webhook security" underline={true} />
        <WebhookSettingsContent />
      </EventContextProvider>
    </Page>
  );
};
